import { Typography } from "@mui/material";
import { ReactNode } from "react";
import { IGroupBlockUI, BooleanMode } from "#next/redux/searchBuilderSlice";
import { RouterOutput, trpc } from "#next/utils/trpc";

type TSavedSearch = RouterOutput["savedSearch"]["list"]["ownedByUser"];

/**
    Takes the various kinds of blocks in a group and returns a consistent
    form that can be used when rendering the layout. This allows the layout
    to be concerned with positioning and separators, and not have to think
    about how to represent each kind of block.
    */
const getTextForBlocks = (group: IGroupBlockUI, searches: TSavedSearch) => {
  const mapped: {
    text: string;
    exclude: boolean;
  }[] = [];

  // Included blocks followed by excluded ones
  const sorted = <T extends { exclude: boolean }>(blocks: T[]) =>
    blocks.toSorted((a, b) => Number(a.exclude) - Number(b.exclude));

  sorted(group.textBlocks).forEach(
    (b) => b.value && mapped.push({ text: `"${b.value}"`, exclude: b.exclude }),
  );
  sorted(group.startsWithBlocks).forEach(
    (b) =>
      b.value &&
      mapped.push({
        text: `starts-with:"${b.value}"`,
        exclude: b.exclude,
      }),
  );
  sorted(group.savedSearchBlocks).forEach(
    (b) =>
      b.savedSearchId &&
      mapped.push({
        text: `project:"${
          searches.find((s) => s.id === b.savedSearchId)?.name
        }"`,
        exclude: b.exclude,
      }),
  );
  sorted(group.usernameBlocks).forEach(
    (b) =>
      b.value &&
      mapped.push({ text: `username:"${b.value}"`, exclude: b.exclude }),
  );

  return mapped;
};

interface IGroupBlockData {
  exclude: boolean;
  depth: number;
  separator: " OR " | " AND ";
  textBlocks: ReturnType<typeof getTextForBlocks>;
  groupBlocks: IGroupBlockData[];
}

/**
 * Takes a group block and returns the information necessary to position
 * it and render its contents
 */
const processGroupBlock = (
  group: IGroupBlockUI,
  depth: number,
  searches: TSavedSearch,
): IGroupBlockData => {
  let separator: IGroupBlockData["separator"];
  switch (group.booleanMode) {
    case BooleanMode.And:
      separator = " AND ";
      break;
    case BooleanMode.Or:
      separator = " OR ";
      break;
    default:
      const res: never = group.booleanMode;
      separator = res;
  }
  return {
    exclude: group.exclude,
    depth,
    separator,
    textBlocks: getTextForBlocks(group, searches),
    groupBlocks: group.groupBlocks.map((child) =>
      processGroupBlock(child, depth + 1, searches),
    ),
  };
};

const colors = ["cyan", "magenta", "yellow", "lightgreen", "orange"];

const generateLayout = (data: IGroupBlockData) => {
  const lines: ReactNode[] = [];
  if (data.textBlocks.length) {
    const line: ReactNode[] = [`(`];
    data.textBlocks.forEach((block, index) => {
      line.push(block.text);
      const nextTextBlock = data.textBlocks[index + 1];
      const nextGroupBlock = data.groupBlocks[0];
      const nextBlock = nextTextBlock ?? nextGroupBlock;
      if (nextBlock) {
        line.push(data.separator);
        if (nextBlock?.exclude) {
          line.push(<span style={{ color: "red" }}>NOT </span>);
        }
      }
      if (!nextTextBlock && !nextGroupBlock) {
        line.push(")");
      }
    });
    lines.push(
      <div
        style={{
          marginLeft: `${data.depth}em`,
          color: colors[data.depth % colors.length],
        }}
      >
        {line}
      </div>,
    );
  } else {
    lines.push(
      <div
        style={{
          marginLeft: `${data.depth}em`,
          color: colors[data.depth % colors.length],
        }}
      >
        &#40;{data.separator}
      </div>,
    );
  }
  if (data.groupBlocks.length) {
    data.groupBlocks.forEach((gb, index) => {
      const innerGroupBlockLines = generateLayout(gb);
      const nextGroupBlocks = data.groupBlocks[index + 1];
      if (nextGroupBlocks) {
        if (nextGroupBlocks.exclude) {
          innerGroupBlockLines.push("NOT ");
        }
      }
      innerGroupBlockLines.forEach((l) => lines.push(l));
    });
    lines.push(
      <div
        style={{
          marginLeft: `${data.depth}em`,
          color: colors[data.depth % colors.length],
        }}
      >
        &#41;
      </div>,
    );
  }
  return lines;
};

export const BooleanPreview = ({ block }: { block: IGroupBlockUI }) => {
  const searchQuery = trpc.savedSearch.list.useQuery();
  const allSearches = searchQuery.data
    ? [...searchQuery.data.ownedByUser, ...searchQuery.data.sharedWithUser]
    : [];

  const mapped = processGroupBlock(block, 0, allSearches);
  const layout = generateLayout(mapped);

  return <Typography>{layout}</Typography>;
};
